import React, { useCallback, useState } from 'react'
import {
  EditableTranslationTableRow,
  EditableTranslationType
} from './EditableTranslationTableRow'
import {
  CustomTranslationsSectionEnum,
  SupportedLocalesEnum
} from '../../../graphql/private/PrivateSchemaTypes'
import { AccountTabSection } from '../TabContentSection'
import { Trans } from '@lingui/macro'
import { FormLabel, Table, Tfoot, Th, Thead, Tr } from '@chakra-ui/react'
import { Flex, FormControl, Heading } from 'front-end/src/components'
import { FixedSizeList as List } from 'react-window'
import { useDebouncedInput } from '../../../app/hooks/useInput'
import { CustomTranslationsTabQuery } from './CustomTranslationsTab.codegen'
import { useControlledSelectFromEnumWithUrlQuery } from '../../../app/hooks/useControlledSelectFromEnum'

type CustomTranslationTabContentProps = {
  translations: CustomTranslationsTabQuery
  bundledLocalisation: Record<string, string>
  localeSelect: ReturnType<
    typeof useControlledSelectFromEnumWithUrlQuery
  >['controlledSelect']
  appSectionSelect: ReturnType<
    typeof useControlledSelectFromEnumWithUrlQuery
  >['controlledSelect']
  selectedLocale: SupportedLocalesEnum
  selectedSection: CustomTranslationsSectionEnum
}

function useClientRect() {
  const [rect, setRect] = useState<DOMRect>()
  const ref = useCallback((node: HTMLTableElement) => {
    if (node !== null) {
      setRect(node.getBoundingClientRect())
    }
  }, [])
  return [rect, ref]
}

function getListHeight(): number {
  return Math.max(window.screen.height - 500, 555)
}

const ITEM_LINE_HEIGHT = 111

export const CustomTranslationTabContent: React.FC<CustomTranslationTabContentProps> =
  ({
    bundledLocalisation,
    translations,
    selectedLocale,
    localeSelect,
    selectedSection,
    appSectionSelect
  }) => {
    const [search, searchInput] = useDebouncedInput({
      debounce: 350,
      autoComplete: 'off'
    })
    const [rect, tableEl] = useClientRect()
    // @ts-expect-error
    let messages: Array<EditableTranslationType> = React.useMemo(
      () =>
        Object.entries(bundledLocalisation.messages).map(
          ([term, translation]) => {
            return {
              term,
              translation,
              locale: selectedLocale as SupportedLocalesEnum,
              app_section: CustomTranslationsSectionEnum.FRONTEND_UI
            }
          }
        ),
      [bundledLocalisation.messages, selectedLocale]
    )
    let apiMessages
    let emailMessages

    if (translations?.backendTranslationMessages) {
      apiMessages = translations.backendTranslationMessages.api.map(
        (singularMessage) => {
          return {
            term: singularMessage.reference, // for API errors we use reference as the term
            translation: singularMessage.definition,
            locale: selectedLocale as SupportedLocalesEnum,
            app_section: CustomTranslationsSectionEnum.API_ERRORS
          }
        }
      ) as Array<EditableTranslationType>

      emailMessages = translations.backendTranslationMessages.email.map(
        (singularMessage) => {
          return {
            term: singularMessage.term,
            translation: singularMessage.definition,
            locale: selectedLocale as SupportedLocalesEnum,
            app_section: CustomTranslationsSectionEnum.EMAILS
          }
        }
      ) as Array<EditableTranslationType>
    }
    switch (selectedSection) {
      case CustomTranslationsSectionEnum.FRONTEND_UI:
        break
      case CustomTranslationsSectionEnum.API_ERRORS:
        messages = apiMessages
        break

      case CustomTranslationsSectionEnum.EMAILS:
        messages = emailMessages
        break
      default:
        messages = messages.concat(apiMessages).concat(emailMessages)
    }

    translations?.organisation?.customTranslationMessages.forEach(
      (customMessage) => {
        const message = messages.find(({ term }) => customMessage.term === term)

        if (message) {
          message.customTranslation = customMessage.translation
        } else {
          messages.push({
            term: customMessage.term,
            customTranslation: customMessage.translation,
            locale: customMessage.locale
          } as EditableTranslationType)
        }
      }
    )

    const filteredMessages = React.useMemo(() => {
      return messages.filter((message) => {
        const lowercasedSearch = search.toLowerCase()
        return (
          message.term.toLowerCase().includes(lowercasedSearch) ||
          (typeof message.translation === 'string' &&
            message.translation?.toLowerCase().includes(lowercasedSearch)) ||
          message.customTranslation?.toLowerCase().includes(lowercasedSearch)
        )
      })
    }, [messages, search])

    return (
      <AccountTabSection>
        <Heading size="md" mt="16px" data-test="CustomTranslationsTitle">
          <Trans>Custom translations</Trans>
        </Heading>
        <Flex mb={4} mt={3}>
          <FormControl id="search" mr={3} data-test="SearchSection">
            <FormLabel>Search</FormLabel>
            {searchInput}
          </FormControl>
          <FormControl id="language" mr={3} data-test="LanguageSelection">
            <FormLabel>
              <Trans>Language</Trans>
            </FormLabel>
            {localeSelect}
          </FormControl>
          <FormControl id="section" data-test="SectionSelect">
            <FormLabel>
              <Trans>Section</Trans>
            </FormLabel>
            {appSectionSelect}
          </FormControl>
        </Flex>
        {/* TODO Remove usage of Table component*/}
        <Table variant="simple" mb={'4px'} ref={tableEl as any}>
          <Thead>
            <Tr>
              <Th width="45%" isNumeric>
                <Trans>Original translation</Trans>
              </Th>
              <Th maxWidth="50%" width="50%">
                <Trans>Translation</Trans>
              </Th>
            </Tr>
          </Thead>
        </Table>
        <List
          height={getListHeight()}
          itemCount={filteredMessages.length}
          itemSize={ITEM_LINE_HEIGHT}
          // @ts-expect-error
          width={rect?.width}
        >
          {({ index, style }) => {
            const message = filteredMessages[index]
            const { term } = message

            return (
              <div style={{ ...style, width: '100%' }}>
                <EditableTranslationTableRow key={term} {...message} />
              </div>
            )
          }}
        </List>
        {/* TODO Remove usage of Table component*/}
        <Table variant="simple">
          <Tfoot>
            <Tr>
              <Th isNumeric width="45%">
                <Trans>Original translation</Trans>
              </Th>
              <Th width="50%">
                <Trans>Translation</Trans>
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </AccountTabSection>
    )
  }
